// START IMPORTS
// Theme imports
import { CircularProgress, Stack, Text } from '@ltvco/refresh-lib/theme';
// Core lib v1 imports
import { useCaptcha } from '@ltvco/refresh-lib/v1';
import {
  searchReport,
  mapReportTypesToReportParams,
  SearchReportResponseData,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useMutation } from '@ltvco/refresh-lib/vendors';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// App-specific imports
import { SidenavContainer } from 'components/sidenav/SidenavContainer';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface CustomWindow extends Window {
  reportVelocityCaptchaCallback?: (response: string) => void;
  hcaptcha?: {
    render: (id: string) => void;
  };
}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const captchaId = 'report-velocity-captcha';
// END CONSTANTS

export const ReportVelocityCaptcha: React.FC = () => {
  // START HOOKS_AND_QUERIES
  const navigate = useNavigate();
  const location = useLocation();
  const [, captchaReady, sitekey] = useCaptcha({
    customConfigResponse: { session: { reportVelocity: true } },
  });
  const permalinkMutation = useMutation({ mutationFn: searchReport });
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const captchaClassName = 'h-captcha';
  const body = location.state?.requestData?.body;
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    if (!location?.state?.requestData) {
      // This route was likely navigated to directly, so redirect to dashboard
      navigate('/dashboard');
    }
  }, [navigate, location]);

  useEffect(() => {
    if (captchaReady) {
      (window as CustomWindow).hcaptcha?.render(captchaId);
    }
  }, [captchaReady]);
  // END EFFECTS

  // START EVENT_HANDLERS
  (window as CustomWindow).reportVelocityCaptchaCallback = async (response) => {
    body['h-captcha-response'] = response;
    const permalink = (
      (await permalinkMutation.mutateAsync(body)) as SearchReportResponseData
    ).report.permalink;
    const reportParam =
      mapReportTypesToReportParams[
        body.report_type as keyof typeof mapReportTypesToReportParams
      ];
    navigate(`/report/${reportParam}?permalink=${permalink}`);
  };
  // END EVENT_HANDLERS

  // START RENDER
  return (
    <SidenavContainer>
      <Stack
        marginTop={6.5}
        spacing={3}
        alignItems={'center'}
        textAlign={'center'}
        padding={3}
      >
        <Stack alignItems={'center'}>
          <Text variant="h3" fontSize={24}>
            Hey there
          </Text>
          <Text variant="h3" fontSize={24}>
            You've generated a lot of reports
          </Text>
        </Stack>
        <Text width={{ xs: '100%', md: '30%' }} margin={'auto'}>
          Please verify that you're not a robot, and then you can continue
          searching as much as you'd like.
        </Text>
        <div
          className={captchaClassName}
          data-sitekey={sitekey}
          id={captchaId}
          data-callback="reportVelocityCaptchaCallback"
        />
        {!captchaReady && (
          <CircularProgress sx={{ color: 'text.primary', p: 1 }} />
        )}
      </Stack>
    </SidenavContainer>
  );
  // END RENDER
};
